import * as React from 'react';
import qs from "query-string";
import uniqBy from "lodash-es/uniqBy";
import { Link, useLocation } from "react-router-dom";
import { Authorization } from "../../misc/components/Authorization";
import { HamburgerMenu } from '../../misc/components/HamburgerMenu';
import { formatDateTime } from "../../misc/components/Formatters/Formatters";
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { Column, Table } from '../../Components/QueryTable';
import { useQuery } from '../../Components/Query';
import { MatchSortHeader } from '../../Components/MatchSortHeader';
import { WebPortalClient, LungVolumeSession } from 'webportalclient';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { SortHeader } from '../../Components/SortHeader';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';
import { DateMatchSortHeader } from '../../Components/DateMatchSortHeader';
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import { useSecurityContext } from '../../Core/SecurityContext';
import { AnalysisAcceptabilityStatus } from '../../misc/components/analysis/AnalysisAcceptabilityStatus';
import { getQueryTableAutomationId, getSessionsTableAutomationId, QueryTableAutomationId, SessionsTableAutomationId } from '../../misc/Helpers/AutomationIdHelper';
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import { OverreadStatusHeader } from '../../Components/OverreadStatusHeader';
import { LungVolumeAnalysisSetTypeEnum } from '../../misc/api/ApiLungVolumeAnalysisSetType';
import { SessionChallengeStatus } from '../../misc/components/SessionChallengeStatus';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';

export interface LungVolumeSessionsListTableRouterProps {
    statusId: string;
}

export interface LungVolumeSessionsListTableProps {
}

declare const rootUrl: string;

export const LungVolumeSessionsListTable = (props: LungVolumeSessionsListTableProps) => {
    const { data, applyFilters, filters } = useQuery();
    const { study, fieldDisplayText } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const { subjectTypes, overreadStatuses } = useReferenceContextProvider();
    const location = useLocation();

    const client = useWebPortalApiClient();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);

    const [analysts, setAnalysts] = React.useState([]);
    const [overreadChallengeStatuses, setOverreadChallengeStatuses] = React.useState([]);

    const statusesOptions = overreadStatuses.map(os => ({
        value: os.id,
        label: os.name
    }))

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("id,number", `studyId eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };


        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("id,number", `site/studyId eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {

            const visits = await client.client.getStudyScheduleVisits("id,label", `studySchedule/studyId eq ${study.id}`, "label");
            const options = visits.value.map(s => ({
                value: s.label,
                label: s.label,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getStudyScheduleSessions("id,label", `studyScheduleVisit/studySchedule/studyId eq ${study.id} and (sessionTypeId eq 'ST_LV_HE' or sessionTypeId eq 'ST_LV_PLETH')`, "label");

            const options = sessions.value.map(s => ({
                value: s.label,
                label: s.label,
            }));

            setSessionOptions(options);
        };

        const getAnalysts = async () => {
            const analysts = await client.client.getLungVolumeAnalysisSets("id, analyst", `session/visit/subject/site/studyId eq ${study.id}`, "analyst");

            const options = uniqBy(analysts.value, o => o.analyst).filter(o => o.analyst).map(s => ({
                value: s.analyst.replace(/'/g, "''"),
                label: s.analyst,
            }));

            setAnalysts(options);
        };

        const getOverreadChallengeStatuses = async () => {
            const overreadChallengeStatuses = await client.client.getOverreadChallengeStatuses();

            const options = uniqBy(overreadChallengeStatuses.value, o => o.name).filter(o => o.name).map(s => ({
                value: s.id,
                label: s.name,
            }));

            setOverreadChallengeStatuses(options);
        };

        getSites();
        getSubjects();
        getVisits();
        getSessions();
        getAnalysts();
        getOverreadChallengeStatuses();

        const queryString = qs.parse(location.search);

        if (queryString.statusId) {
            applyFilters([
                ...filters,
                {
                    field: "status",
                    condition: "eq",
                    values: [queryString.statusId],
                }]);
        }
    }, [location.search]);

    const ViewOverreaderName = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }

    const clearAllFilters = () => {
        applyFilters([]);
    }

    if (subjectTypes.length === 0) {
        return (<LoadingWithMessage message="Loading Lung Volume Sessions..." />);
    }

    const formatAnalyst = analyst => analyst ? analyst : "-";

    const formatStatus = (session: LungVolumeSession) => {
        return (<>
            <AnalysisAcceptabilityStatus value={session.status} /><br />
            <SessionChallengeStatus challengeIsClosed={session.challengeIsClosed} challengeStatus={session.overreadChallengeStatus} analysisOverreadStatus={session.status} />
        </>);
    }

    const formatSessionType = (type: string) => {
        switch (type) {
            case SessionTypes.LungVolumeHelium:
                return "Lung Volume-He";
            case SessionTypes.LungVolumePleth:
                return "Lung Volume-Pleth";
        }
    }

    const RenderHamburgerMenu = (row: LungVolumeSession) => {
        return (
            row.analysisSetId && <HamburgerMenu automationId={`${getQueryTableAutomationId(QueryTableAutomationId.Options)}_${row.id}`}>
                <Authorization permissionId={PermissionEnum.AccessSingleLungVolumeSession} currentPermissions={currentUserPermissions} studyId={study.id}>
                    {row.analysisSets?.find(as => as.type == LungVolumeAnalysisSetTypeEnum.Overread)?.id ? <li>
                        <Link to={`/LungVolume/Analysis/${row.analysisSets.find(as => as.type == LungVolumeAnalysisSetTypeEnum.Overread).id}/view`} data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.ViewAnalysis)}_${row.analysisSets.find(as => as.type == LungVolumeAnalysisSetTypeEnum.Overread).id}`}>View</Link>
                    </li> : null}
                </Authorization>
            </HamburgerMenu>
        );
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Lung Volume Sessions</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button className="btn btn-primary" onClick={clearAllFilters} data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.ClearAllFilters)}`}>
                            Clear All Filters
                        </button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div id="SessionsTable" className="table-responsive" data-automationid="SessionsTable">
                                <Table data={data}>
                                    <Column field="visit" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.SiteNumber)}`}  header={<MatchSortHeader title="Site ID" field="visit/subject/siteId" options={siteOptions} sortField="visit/subject/site/number" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.SiteNumber)}`} />} formatter={visit => visit.subject.site.number} />
                                    <Column field="visit" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.SubjectNumber)}`} header={<MatchSortHeader title={fieldDisplayText.SubjectId} field="visit/subjectId" options={subjectOptions} sortField="visit/subject/number" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.SubjectNumber)}`} />} formatter={visit => visit.subject.number} />
                                    <Column field="visit" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.VisitName)}`} header={<MatchSortHeader title="Visit" field="visit/name" options={visitOptions} sortField="visit/name" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.VisitName)}`} />} formatter={visit => visit.name} />
                                    <Column field="name" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.SessionLabel)}`}  header={<MatchSortHeader title="Session" field="name" options={sessionOptions} sortField="name" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.SessionLabel)}`} />} formatter={name => name} />
                                    <Column field="sessionType" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.SessionType)}`}  header={<SortHeader title="Session Type" field="sessionType" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.SessionType)}`} />} formatter={sessionType => formatSessionType(sessionType)} />
                                    <Column field="occurred" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.StartedAt)}`} header={<DateMatchSortHeader title="Session Start" field="occurred" sortField="occurred" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.StartedAt)}`} />} formatter={occurred => formatDateTime(occurred)} />
                                    <Column field="testsCount" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.TestsCount)}`} header={<SortHeader title="Tests" field="testsCount" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.TestsCount)}`} />} />
                                    <Column field="technician" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.Technician)}`}  header={<SortHeader title="Technician" field="technician" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.Technician)}`} />} formatter={technician => technician} />
                                    <Column field="received" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.LastReceived)}`}  header={<DateMatchSortHeader title="Last Received" field="received" sortField="received" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.LastReceived)}`} />} formatter={ls => formatDateTime(ls)} />
                                    <Column field="" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.Status)}`} 
                                        header={<OverreadStatusHeader overreadAnalysisSetTypeValue={LungVolumeAnalysisSetTypeEnum.Overread} automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.Status)}`} orderField="status" overreadStatuses={statusesOptions} overreadChallengeStatuses={overreadChallengeStatuses} />}
                                        formatter={analysisSet => formatStatus(analysisSet)}
                                    />
                                    {ViewOverreaderName()
                                        ? <Column field="overreader" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.Overreader)}`}  header={<MatchSortHeader title="Over-reader" field="overreader" options={analysts} sortField="overreader" automationId={`${getSessionsTableAutomationId(SessionsTableAutomationId.Overreader)}`} />} formatter={analyst => formatAnalyst(analyst)} />
                                        : null
                                    }
                                    <Column field="" formatter={RenderHamburgerMenu} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
